/**
 * t297u: first-visit quick tips, one registry for every screen. A card shows the first time someone opens a screen
 * and never again once they press Got it (on any device: seen ids are kept server-side in /me/prefs seen_tips).
 * Keep each tip short and plain en-GB; two or three per screen.
 */
export interface ScreenTipSet {
  /** Stored in seen_tips; never rename one (it would show again to everyone). */
  id: string;
  /** The screen's heading on the card. */
  title: string;
  /** Desktop or phone layout. */
  layout: 'desktop' | 'mobile';
  /** Which paths count as this screen. */
  match: (path: string) => boolean;
  tips: string[];
}

export const TIP_SETS: ScreenTipSet[] = [
  {
    id: 'chat', title: 'Chat', layout: 'desktop',
    match: p => p.startsWith('/c/'),
    tips: [
      'Format with **bold**, _italic_ and `code`.',
      'Type @ to mention someone, or # for a channel.',
      'Enter sends; Shift+Enter adds a new line. Hover a message to reply in a thread or react.',
    ],
  },
  {
    id: 'tasks', title: 'Tasks', layout: 'desktop',
    match: p => p === '/tasks',
    tips: [
      'Click a column heading to sort.',
      'Bold means new or changed since you last looked.',
      'Tick rows to act on several at once.',
    ],
  },
  {
    id: 'issues', title: 'Issues', layout: 'desktop',
    match: p => p === '/incidents',
    tips: [
      'Click a column heading to sort. Bold means new or changed since you last looked.',
      'Tick rows to act on several at once.',
      'On your phone you can report an issue with a photo.',
    ],
  },
  {
    id: 'schedule', title: 'Schedule', layout: 'desktop',
    match: p => p === '/workforce/schedule',
    tips: [
      'Click the Position heading to filter.',
      'Drag a shift to move it; hold Shift to copy it.',
      'Hours sets the day view’s range. Press ? for shortcuts.',
    ],
  },
  {
    id: 'm_home', title: 'Home', layout: 'mobile',
    match: p => p === '/',
    tips: [
      'Tap a card to open it.',
      'Long-press a card to react; press and hold a reaction to see who reacted.',
      'Pull down at the top to refresh.',
    ],
  },
];

export function tipsFor(path: string, layout: 'desktop' | 'mobile'): ScreenTipSet | null {
  return TIP_SETS.find(s => s.layout === layout && s.match(path)) ?? null;
}

/**
 * Suppression on this device: ?tips=off or ?tips=on (remembered in localStorage `hub.tips`), and automated browsers
 * (screenshots, smoke tests) never see tips unless opted in with 'on'. The smoke-test account is also off server-side.
 */
export const TIPS_FLAG_KEY = 'hub.tips';
export type TipsFlag = 'on' | 'off' | null;

export function readTipsFlag(search: string): TipsFlag {
  const q = new URLSearchParams(search).get('tips');
  try {
    if (q === 'on' || q === 'off') { localStorage.setItem(TIPS_FLAG_KEY, q); return q; }
    const v = localStorage.getItem(TIPS_FLAG_KEY);
    return v === 'on' || v === 'off' ? v : null;
  } catch { return q === 'on' || q === 'off' ? q : null; }
}

export function tipsAllowed(flag: TipsFlag, webdriver: boolean): boolean {
  if (flag === 'off') return false;
  if (flag === 'on') return true;
  return !webdriver;
}
